import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, FileText, Download, ExternalLink, AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { supabase } from '../../../config/supabase';

export default function DocumentViewerModal({ url, title, onClose }) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isPdf, setIsPdf] = useState(false);
  const [useGoogleViewer, setUseGoogleViewer] = useState(false);
  const [resolvedUrl, setResolvedUrl] = useState('');
  // When an <img> fails (e.g. the file is actually a PDF or an unusual format),
  // fall back to a native <iframe> which the browser can render directly.
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    console.log(`[DocumentViewer] Opening: "${title}"`);
    console.log(`[DocumentViewer] URL:`, url);

    if (!url) {
      setHasError(true);
      setErrorMessage("Aucune URL de document valide n'a été fournie (Lien manquant ou vide).");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setHasError(false);
    setErrorMessage('');
    setIsPdf(false);
    setUseGoogleViewer(false);
    setImgFailed(false);

    const setupViewer = (targetUrl) => {
      setResolvedUrl(targetUrl);

      // Quick local detection from URL (works for most cases where the URL has .pdf)
      const urlLower = targetUrl.toLowerCase().split('?')[0];
      if (targetUrl.startsWith('data:application/pdf') || urlLower.endsWith('.pdf')) {
        console.log('[DocumentViewer] PDF detected from URL');
        setIsPdf(true);
        setIsLoading(false);
        return;
      }

      // For remote files: do a HEAD request to get the real Content-Type.
      if (targetUrl.startsWith('http')) {
        fetch(targetUrl, { method: 'HEAD' })
          .then((res) => {
            console.log(`[DocumentViewer] HEAD ${res.status} | Content-Type: ${res.headers.get('Content-Type')}`);

            if (res.status === 403) {
              setHasError(true);
              setErrorMessage("Accès refusé (403). L'autorisation a peut-être expiré.");
              return;
            }
            if (res.status === 404) {
              setHasError(true);
              setErrorMessage("Fichier introuvable (404). Le document a peut-être été supprimé.");
              return;
            }

            const ct = (res.headers.get('Content-Type') || '').toLowerCase();
            if (ct.includes('application/pdf')) {
              console.log('[DocumentViewer] PDF detected from Content-Type header');
              setIsPdf(true);
            }
          })
          .catch((err) => {
            // CORS may block HEAD requests — safe to ignore, just proceed
            console.warn('[DocumentViewer] HEAD request blocked (CORS/network), proceeding anyway:', err.message);
          })
          .finally(() => {
            setIsLoading(false);
          });
      } else {
        setIsLoading(false);
      }
    };

    // Check if the URL is private or a legacy public URL in the secure bucket
    let bucket = '';
    let filePath = '';

    if (url.startsWith('private://')) {
      const match = url.match(/^private:\/\/([^\/]+)\/(.+)$/);
      if (match) {
        bucket = match[1];
        filePath = match[2];
      }
    } else if (url.includes('/storage/v1/object/public/car-track-docs/')) {
      const match = url.match(/\/storage\/v1\/object\/public\/(car-track-docs)\/(.+)$/);
      if (match) {
        bucket = match[1];
        filePath = match[2];
      }
    }

    if (bucket && filePath) {
      console.log(`[DocumentViewer] Resolving secure storage file: ${bucket}/${filePath}`);
      supabase.storage
        .from(bucket)
        .createSignedUrl(filePath, 3600) // 1 hour token
        .then(({ data, error }) => {
          if (error) throw error;
          if (!data?.signedUrl) throw new Error("Url signée vide");
          setupViewer(data.signedUrl);
        })
        .catch(err => {
          console.error('[DocumentViewer] Failed to resolve secure URL:', err);
          setHasError(true);
          setErrorMessage("Échec d'obtention de l'autorisation d'accès au fichier : " + err.message);
          setIsLoading(false);
        });
    } else {
      setupViewer(url);
    }
  }, [url, title]);

  const googleViewerUrl = resolvedUrl ? `https://docs.google.com/viewer?url=${encodeURIComponent(resolvedUrl)}&embedded=true` : '';

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 animate-fade-in"
      style={{ zIndex: 999999 }}
    >
      <div className="relative w-full max-w-5xl h-[85vh] flex flex-col bg-gray-950 rounded-2xl overflow-hidden shadow-2xl border border-gray-800 animate-scale-in">

        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-gray-900/90 border-b border-gray-800 shrink-0">
          <h3 className="text-white font-bold text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-400 shrink-0" />
            <span className="truncate max-w-[200px] md:max-w-md" title={title}>{title}</span>
          </h3>
          <div className="flex items-center gap-2">
            {resolvedUrl && (
              <>
                <a
                  href={resolvedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3.5 py-1.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg flex items-center gap-1.5 text-xs font-semibold border border-gray-700 hover:border-gray-600 transition"
                  title="Ouvrir dans un nouvel onglet"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Ouvrir</span>
                </a>
                <a
                  href={resolvedUrl}
                  download={`Document_${(title || 'fichier').replace(/\s+/g, '_')}`}
                  className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-1.5 text-xs font-semibold shadow transition"
                  title="Télécharger le fichier"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Télécharger</span>
                </a>
              </>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white bg-gray-900 hover:bg-gray-800 rounded-lg transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-auto bg-black flex flex-col justify-center items-center p-4 relative min-h-[50vh]">

          {/* Loading state */}
          {isLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-950/80 z-10 gap-3">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              <span className="text-sm font-semibold text-gray-400">Chargement du document...</span>
            </div>
          )}

          {/* Error State */}
          {!isLoading && hasError ? (
            <div className="flex flex-col items-center justify-center text-center p-6 bg-red-950/10 border border-red-900/50 rounded-2xl max-w-md">
              <AlertTriangle className="w-12 h-12 text-red-500 mb-3" />
              <h4 className="text-white font-bold text-base mb-1">Erreur de chargement</h4>
              <p className="text-gray-400 text-sm mb-5 leading-relaxed">{errorMessage}</p>
              {resolvedUrl && (
                <a
                  href={resolvedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-red-900 hover:bg-red-800 text-white text-xs font-bold rounded-lg transition flex items-center gap-1.5"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Tenter l'ouverture directe
                </a>
              )}
            </div>

          ) : !isLoading && isPdf ? (
            /* ── PDF Viewer ── */
            <div className="w-full h-full flex flex-col gap-2">
              {/* Info banner with toggle */}
              <div className="w-full bg-blue-950/20 border border-blue-900/40 rounded-xl p-3 text-xs text-blue-300 flex items-center justify-between gap-3 shrink-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm">💡</span>
                  <span>
                    {useGoogleViewer
                      ? 'Affichage via Google Docs Viewer.'
                      : 'Si le PDF est vide, utilisez le viewer alternatif ou ouvrez directement.'}
                  </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => setUseGoogleViewer(v => !v)}
                    className="px-2.5 py-1 bg-blue-900/60 hover:bg-blue-800 text-white font-bold rounded-lg transition flex items-center gap-1"
                    title={useGoogleViewer ? 'Revenir au viewer natif' : 'Essayer le viewer alternatif'}
                  >
                    <RefreshCw className="w-3 h-3" />
                    {useGoogleViewer ? 'Natif' : 'Alternatif'}
                  </button>
                  <a
                    href={resolvedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-2.5 py-1 bg-blue-900 hover:bg-blue-800 text-white font-bold rounded-lg transition flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Ouvrir
                  </a>
                </div>
              </div>
              {/* Iframe — native or Google Docs viewer */}
              <iframe
                key={useGoogleViewer ? 'google' : 'native'}
                src={useGoogleViewer ? googleViewerUrl : resolvedUrl}
                className="w-full flex-1 bg-white rounded-xl border border-gray-800 min-h-[300px]"
                title={title}
                type={useGoogleViewer ? undefined : 'application/pdf'}
                onLoad={() => setIsLoading(false)}
              />
            </div>

          ) : !isLoading && imgFailed ? (
            /* ── Fallback: render the file natively in an iframe (handles PDFs,
                 images and other formats the browser can display directly). ── */
            <iframe
              src={resolvedUrl}
              className="w-full h-full bg-white rounded-xl border border-gray-800 min-h-[300px]"
              title={title}
              onLoad={() => setIsLoading(false)}
            />
          ) : !isLoading ? (
            /* ── Image Viewer ── */
            <div className="w-full h-full flex items-center justify-center">
              <img
                src={resolvedUrl}
                className="max-w-full max-h-[70vh] object-contain rounded-xl shadow-2xl border border-gray-800"
                alt={title}
                onLoad={() => setIsLoading(false)}
                onError={() => {
                  // Not a displayable image → switch to the native iframe fallback.
                  console.warn('[DocumentViewer] <img> failed, falling back to iframe for', resolvedUrl);
                  setImgFailed(true);
                  setIsLoading(false);
                }}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
}
